/**
 * Merkezi oturum yönetimi.
 *
 * Yetki yükseltme bulgusu kapsamında eklenmiştir. Kullanıcı rolü artık
 * sign-in yanıtının gövdesinden ya da localStorage'dan değil, yalnızca
 * imzalı JWT'nin içinden okunur. Rol bilgisi localStorage'a hiç yazılmaz.
 *
 * Uygulamadaki tüm `userRole` okumaları `getUserInfo()` üzerinden geçer;
 * bu sayede sayfa kodlarında yapı değişikliği gerekmez.
 */

import { CookieName, userTypeEnum } from 'app/shared/enums'

/**
 * Rolün JWT payload'ında hangi anahtarda durabileceği.
 * Sıra önceliklidir; arama büyük/küçük harfe duyarsızdır.
 *
 * Mevcut API `UserRole` claim'ini sayısal string olarak üretiyor
 * (ör. `"UserRole": "1"`). Diğerleri olası varyasyonlar için yedektir.
 */
export const ROLE_CLAIM_KEYS = [
  'userrole',
  'role',
  'roles',
  'http://schemas.microsoft.com/ws/2008/06/identity/claims/role',
]

/** Rol adıyla gelen claim'leri sayısal role çevirmek için. */
const ROLE_NAME_MAP: { [key: string]: number } = Object.keys(userTypeEnum)
  .filter((key) => isNaN(Number(key)))
  .reduce((acc: { [key: string]: number }, key: string) => {
    acc[key.toLowerCase()] = (userTypeEnum as any)[key]
    return acc
  }, {})

/** localStorage'da oturumla birlikte temizlenecek anahtarlar. */
const SESSION_STORAGE_KEYS = [
  CookieName.Token,
  CookieName.UserInformation,
  CookieName.UserPermission,
  CookieName.UserSpecialPermission,
]

const EXTRA_SESSION_KEYS = ['brandListCache']

export interface TokenPayload {
  [key: string]: any
  exp?: number
}

/** base64url -> UTF-8 metin. */
const decodeBase64Url = (value: string): string => {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/')
  const padded = normalized + '==='.slice((normalized.length + 3) % 4)
  const binary = window.atob(padded)

  // atob latin1 döner; UTF-8 karakterler için yeniden çözümlenir
  return decodeURIComponent(
    binary
      .split('')
      .map((char) => '%' + ('00' + char.charCodeAt(0).toString(16)).slice(-2))
      .join('')
  )
}

/** JWT'nin payload bölümünü çözer; token bozuksa null döner. */
export const decodeJwtPayload = (token: any): TokenPayload | null => {
  if (!token || typeof token !== 'string') {
    return null
  }

  const parts = token.split('.')

  if (parts.length < 2) {
    return null
  }

  try {
    const parsed = JSON.parse(decodeBase64Url(parts[1]))

    return parsed && typeof parsed === 'object' ? parsed : null
  } catch (error) {
    return null
  }
}

export const getToken = (): string | null =>
  localStorage.getItem(CookieName.Token)

// Aynı token için tekrar tekrar çözümleme yapılmaması adına basit önbellek
let cachedToken: string | null = null
let cachedPayload: TokenPayload | null = null
let cachedLowerKeyPayload: TokenPayload | null = null

/** Claim adları büyük/küçük harf farkıyla gelebildiği için anahtarlar küçültülür. */
const toLowerKeyMap = (payload: TokenPayload): TokenPayload =>
  Object.keys(payload).reduce((acc: TokenPayload, key: string) => {
    acc[key.toLowerCase()] = payload[key]
    return acc
  }, {})

/** Geçerli oturum token'ının payload'ı. */
export const getTokenPayload = (): TokenPayload | null => {
  const token = getToken()

  if (!token) {
    cachedToken = null
    cachedPayload = null
    cachedLowerKeyPayload = null
    return null
  }

  if (token !== cachedToken) {
    cachedToken = token
    cachedPayload = decodeJwtPayload(token)
    cachedLowerKeyPayload = cachedPayload ? toLowerKeyMap(cachedPayload) : null
  }

  return cachedPayload
}

/** Payload'ı küçük harfli anahtarlarla döner (claim araması için). */
const getLowerKeyPayload = (): TokenPayload | null => {
  getTokenPayload()

  return cachedLowerKeyPayload
}

/** Token'daki bir claim'i adına göre (harf duyarsız) okur. */
export const getTokenClaim = (name: string): any =>
  getLowerKeyPayload()?.[String(name).toLowerCase()]

/** Claim değerini sayısal role çevirir; çözülemezse null. */
const normalizeRoleValue = (value: any): number | null => {
  if (value === null || value === undefined) {
    return null
  }

  // Claim dizi olarak gelebilir: ["1"] ya da ["SuperAdmin"]
  if (Array.isArray(value)) {
    for (let index = 0; index < value.length; index++) {
      const resolved = normalizeRoleValue(value[index])

      if (resolved !== null) {
        return resolved
      }
    }

    return null
  }

  if (typeof value === 'number') {
    return userTypeEnum[value] !== undefined ? value : null
  }

  if (typeof value === 'string') {
    const trimmed = value.trim()

    if (/^\d+$/.test(trimmed)) {
      const numeric = Number(trimmed)

      return userTypeEnum[numeric] !== undefined ? numeric : null
    }

    const mapped = ROLE_NAME_MAP[trimmed.toLowerCase()]

    return mapped === undefined ? null : mapped
  }

  return null
}

/**
 * Kullanıcının rolü. Yalnızca imzalı token'dan okunur;
 * token'da geçerli bir rol claim'i yoksa null döner.
 */
export const getUserRole = (): number | null => {
  const payload = getLowerKeyPayload()

  if (!payload) {
    return null
  }

  for (let index = 0; index < ROLE_CLAIM_KEYS.length; index++) {
    const resolved = normalizeRoleValue(payload[ROLE_CLAIM_KEYS[index]])

    if (resolved !== null) {
      return resolved
    }
  }

  return null
}

/** Token'ın süresi dolmuş mu? `exp` claim'i yoksa dolmamış sayılır. */
export const isTokenExpired = (): boolean => {
  const payload = getTokenPayload()

  if (!payload || typeof payload.exp !== 'number') {
    return false
  }

  return payload.exp * 1000 <= Date.now()
}

/** localStorage'daki profil bilgisi (rol alanı yok sayılır). */
const getStoredUserInfo = (): { [key: string]: any } => {
  try {
    const parsed = JSON.parse(
      localStorage.getItem(CookieName.UserInformation) || '{}'
    )

    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch (error) {
    return {}
  }
}

/**
 * Oturum açmış kullanıcının bilgileri.
 *
 * Profil alanları (ad, e-posta, relationId…) localStorage'dan gelir;
 * `userRole` her zaman token'dan türetilir ve localStorage'daki bir
 * değerle değiştirilemez.
 */
export const getUserInfo = (): { [key: string]: any } => {
  const stored = getStoredUserInfo()

  return {
    ...stored,
    userRole: getUserRole(),
  }
}

/** Oturum sahibinin id'si; öncelik token'daki `UserId` claim'indedir. */
export const getUserId = (): any => {
  const claim = getTokenClaim('UserId')

  if (claim !== undefined && claim !== null && claim !== '') {
    const numeric = Number(claim)

    return isNaN(numeric) ? claim : numeric
  }

  return getStoredUserInfo()?.id
}

/**
 * Yalnızca profil bilgisini tazeler (ör. `auth/user-info` yanıtı).
 * Roldeki tek kaynak token olduğu için `userRole` alanı yazılmaz.
 */
export const setUserProfile = (userInfo: any): void => {
  const { userRole, ...profile } = userInfo || {}

  localStorage.setItem(CookieName.UserInformation, JSON.stringify(profile))
}

/** Oturumla ilgili tüm localStorage kayıtlarını siler. */
export const clearSession = (): void => {
  SESSION_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key))
  EXTRA_SESSION_KEYS.forEach((key) => localStorage.removeItem(key))

  cachedToken = null
  cachedPayload = null
  cachedLowerKeyPayload = null
}

export interface SignInResponse {
  token: string
  userInfo?: any
  permissions?: any
  specialPermissions?: any
}

/**
 * Oturumu kurar. Sign-in, kullanıcı değiştirme ve e-posta doğrulama
 * akışlarının tamamı bu fonksiyonu kullanır.
 *
 * `userInfo.userRole` bilinçli olarak saklanmaz: rol tek kaynaktan,
 * token'dan okunur. Böylece yanıt gövdesindeki rolü değiştirmek
 * uygulamada bir yetki değişikliğine yol açmaz.
 */
export const setSession = (response: SignInResponse): void => {
  localStorage.setItem(CookieName.Token, response.token)

  const { userRole, ...profile } = response.userInfo || {}

  localStorage.setItem(CookieName.UserInformation, JSON.stringify(profile))
  localStorage.setItem(
    CookieName.UserPermission,
    JSON.stringify(response.permissions)
  )
  localStorage.setItem(
    CookieName.UserSpecialPermission,
    JSON.stringify(response.specialPermissions)
  )

  cachedToken = null
  cachedPayload = null
  cachedLowerKeyPayload = null
}

/** Token var mı, süresi geçerli mi ve rol çözülebiliyor mu? */
export const isSessionValid = (): boolean =>
  !!getToken() && !isTokenExpired() && getUserRole() !== null

/**
 * Oturum doğrulanamıyorsa kayıtları temizleyip giriş sayfasına yönlendirir.
 * Token'da rol claim'i bulunamadığında localStorage'daki bir değere
 * düşmek yerine oturum sonlandırılır.
 */
export const enforceValidSession = (): boolean => {
  if (!getToken()) {
    return false
  }

  if (isSessionValid()) {
    return true
  }

  clearSession()

  const language = localStorage.getItem(CookieName.Language) || ''
  window.location.href = language ? `/${language}/signin` : '/signin'

  return false
}
